// src/services/cognixAgentService.js
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Create axios instance with auth headers
const createAuthenticatedRequest = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  
  if (!token) {
    console.error('❌ No authentication token found');
    throw new Error('No authentication token found. Please login again.');
  }
  
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

// Helper function to handle API errors
const handleApiError = (error, operation) => {
  console.error(`❌ ${operation} failed:`, error);
  
  if (error.response?.status === 401) {
    throw new Error('Authentication failed. Please login again.');
  } else if (error.response?.status === 403) {
    throw new Error('Access denied. You do not have permission for this operation.');
  } else if (error.response?.status === 404) {
    throw new Error('Resource not found.');
  } else if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map(e => e.msg || JSON.stringify(e)).join('; ');
      throw new Error(msgs);
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  } else if (error.message) {
    throw new Error(error.message);
  } else {
    throw new Error(`${operation} failed. Please try again.`);
  }
};

export const cognixAgentService = {
  // Create a new Cognix Agent
  createAgent: async (agentData) => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.post('/api/cognix-agents/create', agentData);
      if (response.data?.success === false) {
        throw new Error(response.data.error || 'Failed to create agent');
      }
      console.log('✅ createAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Creating Cognix Agent');
    }
  },

  // List all Cognix Agents for the current user
  listAgents: async () => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.get('/api/cognix-agents/list');
      console.log('✅ listAgents success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Listing Cognix Agents');
    }
  },

  // Get a specific Cognix Agent by ID
  getAgent: async (agentId) => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.get(`/api/cognix-agents/${agentId}`);
      console.log('✅ getAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Getting Cognix Agent');
    }
  },

  // Update a Cognix Agent
  updateAgent: async (agentId, updates) => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.put(`/api/cognix-agents/${agentId}`, updates);
      if (response.data?.success === false) {
        throw new Error(response.data.error || 'Failed to update agent');
      }
      console.log('✅ updateAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Updating Cognix Agent');
    }
  },

  // Delete a Cognix Agent
  deleteAgent: async (agentId) => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.delete(`/api/cognix-agents/${agentId}`);
      console.log('✅ deleteAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Deleting Cognix Agent');
    }
  },

  // Execute a query using a Cognix Agent
  executeAgent: async (agentId, userQuery, chatHistory = []) => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.post('/api/cognix-agents/execute', {
        agent_id: agentId,
        user_query: userQuery,
        chat_history: chatHistory
      });
      console.log('✅ executeAgent success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Executing Cognix Agent query');
    }
  },

  // Execute a Cognix Agent query with SSE streaming
  // onEvent(event) is called for each SSE event: tool_start, tool_end, token, done, error
  executeAgentStream: async (agentId, userQuery, chatHistory = [], onEvent) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) throw new Error('No authentication token found. Please login again.');

    const response = await fetch(`${API_BASE_URL}/api/cognix-agents/execute-stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ agent_id: agentId, user_query: userQuery, chat_history: chatHistory })
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errText || response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep any incomplete line
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6));
            onEvent(event);
          } catch {
            // skip malformed lines
          }
        }
      }
    }
  },

  // Get execution history for a Cognix Agent
  getExecutionHistory: async (agentId, page = 1, pageSize = 20) => {
    try {
      const api = createAuthenticatedRequest();
      const response = await api.get(`/api/cognix-agents/${agentId}/execution-history`, {
        params: { page, page_size: pageSize }
      });
      console.log('✅ getExecutionHistory success:', response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, 'Getting execution history');
    }
  }
};

export default cognixAgentService;
